import * as React from 'react';
import injectSheet from 'react-jss';
import { Divider, Icon } from 'antd';
import { connect, ConnectedProps } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router';
import { closeSideMenu } from '../../actions/navigationActions';
import { Link } from 'react-router-dom';
import {
  getDashboardRoute,
  getAllDashboardsRoute,
} from '../../routing/utils';
import { getStarredDashboards } from 'src/selectors/getStarredDashboards';
import { NAVBAR_HEIGHT } from 'src/globals';
import { mediaDown } from 'src/design-system/mixins/responsive';
import { getSideMenuList } from '../../utils/partsToShow/sideMenuList';
import { getPartsMap } from '../../utils/partsToShow/partsMap';
import * as partsConstants from '../../utils/partsToShow/partsConstants';
import * as sidebarSectionConstants from '../../utils/partsToShow/sidebarSectionConstants';
import { RouterProps, StylesProps, valueof } from 'src/Library/Types';
import { applestoreUrl, homequickLink, linksToHide, paidFeatures, playstoreURL, quickLinks, sectionIcons, websiteLinks } from './utils';
import CrownIcon from '../common/icons/Crown';
import { RouteMapping } from 'src/utils/utils';

const shipsyLogo = require('src/assets/images/ShipsyLogo.svg');
const playstoreBadge = require('src/assets/images/Playstore.svg');
const applestoreBadge = require('src/assets/images/Applestore.svg');

const SIDE_MENU_Z_INDEX = 110000;

const styles = theme => ({
  flex: {
    display: 'flex',
  },
  logo: {
    color: theme.colors.bgWhite,
  },
  header: {
    minHeight: 80,
    display: 'flex',
    justifyContent: 'space-between',
    padding: `22px ${theme.spacing.lg}px`,
    gap: `${theme.spacing.xxs}px`,
  },
  quickLinks: {
    height: 32,
    backgroundColor: theme.colors.bgSubtle,
    borderRadius: 16,
    color: theme.colors.text,
    fontWeight: 600,
    fontSize: theme.fontSize.body,
    padding: `9px ${theme.spacing.md}px`,
    margin: `0 ${theme.spacing.xxs}px`,
    lineHeight: 1,
    '&:hover': {
      background: theme.colors.primaryDark,
      color: theme.colors.bgWhite,
    },
  },
  homeLinks: {
    height: 32,
    backgroundColor: theme.colors.bgSubtle,
    borderRadius: 16,
    color: theme.colors.text,
    fontWeight: 600,
    fontSize: theme.fontSize.body,
    padding: `9px ${theme.spacing.md}px`,
    margin: `0 ${theme.spacing.xxs}px`,
    lineHeight: 1,
    '&:hover': {
      background: theme.colors.primaryDark,
      color: theme.colors.bgWhite,
    },
  },
  subMenuList: {
    color: theme.colors.text,
    borderRadius: theme.radius.md,
    minHeight: 82,
    backgroundColor: theme.colors.primaryDark,
    fontSize: theme.fontSize.h2,
    fontWeight: 'bold',
    padding: theme.spacing.md,
    marginRight: theme.spacing.md,
    marginBottom: theme.spacing.md,
    zIndex: theme.zIndex.dropdown + 2,
    position: 'relative',
  },
  subMenuListHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    color: theme.colors.bgWhite,
  },
  subMenuListDescription: {
    fontSize: theme.fontSize.caption,
    fontWeight: 500,
    width: 176,
    height: 'auto',
    minHeight: 26,
    lineHeight: 1.2,
    marginBottom: theme.spacing.md,
    wordWrap: 'break-word',
    whiteSpace: 'normal',
  },
  subMenu: {
    color: theme.colors.bgWhite,
    fontWeight: 600,
    margin: `${theme.spacing.sm}px 0`,
    fontSize: theme.fontSize.h3,
  },
  menuIcon: {
    width: 40,
  },
  menuItem: {
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    color: theme.colors.text,
    borderRadius: theme.radius.md,
    height: 80,
    backgroundColor: theme.colors.bgWhite,
    fontSize: theme.fontSize.h2,
    fontWeight: 'bold',
    padding: theme.spacing.md,
    marginRight: theme.spacing.md,
    marginBottom: theme.spacing.md,
    paddingBottom: 0,
    '& img': {
      display: 'none',
    },
    '&:hover': {
      background: theme.colors.primaryDark,
      color: theme.colors.bgWhite,
      height: 'auto', // Allow menu item to expand on hover
      minHeight: 80,
      '& >div >div': {
        color: theme.colors.bgWhite,
        fontSize: 8,
        whiteSpace: 'normal',
        height: 'auto',
        minHeight: 26,
      },
      '& >div:first-child': {
        width: 176,
        overflow: 'visible', // Changed from hidden to visible
        textOverflow: 'clip', // Changed from ellipsis to clip
        whiteSpace: 'normal',
      },
      '& img': {
        display: 'block',
      },
    },
  },
  menuDescription: {
    color: theme.colors.textSecondary,
    fontSize: theme.fontSize.caption,
    fontWeight: 500,
    width: 180,
    height: 'auto',
    minHeight: 26,
    lineHeight: 1.2,
    wordWrap: 'break-word',
    whiteSpace: 'normal',
  },
  menuGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    borderRight: `1px solid ${theme.colors.border}`,
  },
  setupGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    marginBottom: 44,
  },
  setupOptions: {
    fontSize: theme.fontSize.small,
    color: theme.colors.text,
    fontWeight: 600,
    margin: `${theme.spacing.ms}px 0`,
    '&:hover': {
      color: theme.colors.primaryDark,
      textDecoration: 'underline',
    },
  },
  appLinkBody: {
    fontSize: theme.fontSize.caption,
    marginBottom: 10,
    lineHeight: 1,
    color: theme.colors.text,
  },
  overlay: {
    position: 'fixed',
    top: NAVBAR_HEIGHT,
    bottom: 0,
    left: 0,
    right: 0,
    opacity: 0.45,
    zIndex: SIDE_MENU_Z_INDEX,
    backgroundColor: '#000000',
    display: 'block',
  },
  dropdown: {
    backgroundColor: theme.colors.bgWhite,
    minHeight: 80,
    width: '100%',
    position: 'fixed',
    top: NAVBAR_HEIGHT,
    zIndex: SIDE_MENU_Z_INDEX + 1,
  },
  menuItemGrid: {
    width: '70%',
    padding: theme.spacing.lg,
    ...mediaDown(theme.breakpoint.md, {
      width: '100%',
    }),
  },
  setupMenu: {
    width: '30%',
    padding: theme.spacing.lg,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    ...mediaDown(theme.breakpoint.md, {
      width: '100%',
    }),
  },
  ...mediaDown(theme.breakpoint.md, {
    menuGrid: {
      gridTemplateColumns: '1fr 1fr',
    },
  }),
  ...mediaDown(theme.breakpoint.sm, {
    hideLayout: {
      display: 'none',
    },
    menuGrid: {
      gridTemplateColumns: '1fr',
    },
    dropdown: {
      overflowY: 'auto',
      maxHeight: '80vh',
    },
  }),
});

interface IProps extends StylesProps<ReturnType<typeof styles>>, ReduxProps, RouterProps { };

const SideMenu: React.FC<IProps> = (props) => {
  const {
    closeSideMenu,
    classes,
    open,
    sidebarSectionsToShow,
    partsToShow,
    dashboards,
    type,
    history,
    orgType,
    showProcurement,
    defaultLandingPage,
    userLandingPage,
    discoverRatesPrefillData,
    orgLogo,
    showShipperLogoInsteadOfShipsy
  } = props;

  const dropdownRef = React.useRef<HTMLDivElement | null>(null);

  const [state, setState] = React.useState({
    navLogo: window.localStorage.logoUrl,
    sectionList: getSideMenuList,
    partsMap: getPartsMap(props.permissionsModule, props.type),
  });
  const [expandedMenu, setExpandedMenu] = React.useState<string>();

  React.useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) {
        return;
      }
      if (dropdownRef.current && dropdownRef.current.contains(target)) {
        return;
      }
      closeSideMenu();
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [open, closeSideMenu]);

  const { sectionList, partsMap } = state;

  // Get current route path
  const currentPath = history.location.pathname;
  
  // Create RouteMapping to check against
  const viewType = showProcurement ? 'buy' : 'sell';
  const redirectToSpotRatesLandingPage = orgType === 'SHIPPER' && !discoverRatesPrefillData;
  const routeParams = {
    viewType,
    redirectToSpotRatesLandingPage
  };
  const RouteMappingObj = RouteMapping(routeParams);
  
  // Use userLandingPage if available, otherwise fallback to defaultLandingPage
  const activeLandingPage = userLandingPage || defaultLandingPage;
  
  // Check if current path matches the active landing page route
  const isOnActiveLandingPage = activeLandingPage && 
    RouteMappingObj[activeLandingPage] && 
    currentPath === RouteMappingObj[activeLandingPage];

  const renderQuickLinks = () => {
    return (
      <div className={classes.flex}>
        {quickLinks.map(subMenuId => {
          const subMenu = partsMap[subMenuId];
          if (!partsToShow[subMenuId] || !subMenu) {
            return null;
          }

          return (
            <Link
              key={subMenuId}
              to={() => handleRedirect(subMenu.route, subMenu.rootTitle)}
              onClick={closeSideMenu}
            >
              <div className={classes.quickLinks}>
                {subMenu.sectionTitle}
              </div>
            </Link>
          );
        })}
      </div>
    )
  }

  const renderHomeQuickLink = () => {
    return (
      <div>
        {homequickLink.map((subMenuId, index) => {
          const subMenu = partsMap[subMenuId];
          if (!subMenu) {
            return null;
          }
          
          return (
            <React.Fragment key={subMenuId}>
              {isOnActiveLandingPage ? (
                <div 
                  className={classes.homeLinks}
                  style={{
                    opacity: 0.6,
                    cursor: 'not-allowed',
                    pointerEvents: 'none'
                  }}
                >
                  {subMenu.sectionTitle}
                </div>
              ) : (
                <Link
                  to={() => handleRedirect(subMenu.route, subMenu.rootTitle)}
                  onClick={closeSideMenu}
                >
                  <div className={classes.homeLinks}>
                    {subMenu.sectionTitle}
                  </div>
                </Link>
              )}
              {index < homequickLink.length - 1 && <span style={{ margin: '0 4px' }}>|</span>}
            </React.Fragment>
          );
        })}
      </div>
    )
  }

  const renderLogo = () => {
    return (
      <div className={classes.logo}>
        {showShipperLogoInsteadOfShipsy ? (
          <img src={orgLogo} height={32} alt='Organisation Logo' />
        ) : (
          <img src={shipsyLogo} height={32}  alt='Shipsy Logo'/>
        )}
      </div>
    );
  }

  const renderHeader = () => {
    return (
      <div className={classes.header}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {renderHomeQuickLink()}
          <Divider 
            type="vertical" 
            style={{ 
              height: '32px', 
              margin: '0 4px',
              borderColor: '#D9D9D9'
            }}
          />
          {renderQuickLinks()}
        </div>
        {renderLogo()}
      </div>
    )
  }

  const showSubMenus = (menuId: string) => {
    setExpandedMenu(menuId);
  }

  const hideSubMenus = () => {
    setExpandedMenu(undefined);
  }

  const handleRedirect = (pathname: string, rootTitle: string) => {
    return {
      pathname,
      state: {
        rootTitle,
      }
    }
  }

  const renderDashboardSubMenu = () => {
    if (!partsToShow.dashboard) {
      return null;
    }

    const dashboardSubMenus: React.ReactElement[] =
      dashboards.map(elem => {
        const subMenuId = `dashboard_${elem.id}`;
        const subMenu = {
          route: getDashboardRoute(elem.id),
          rootTitle: 'dashboard',
          sectionTitle: elem.name,
        };
        return renderSubMenu(subMenuId, subMenu);
      });

    dashboardSubMenus.push(
      renderSubMenu('allDashboard', {
        route: getAllDashboardsRoute(),
        rootTitle: 'dashboard',
        sectionTitle: 'All Dashboards',
      })
    );

    return dashboardSubMenus;
  }

  const renderSubMenu = (id, item: { route, rootTitle, sectionTitle?, hideOnMobile?}) => {
    return (
      <Link
        to={() => handleRedirect(item.route, item.rootTitle)}
        onClick={closeSideMenu}
      >
        <div
          className={classes.subMenu}
          key={id}
        >
          {item.sectionTitle}
        </div>
      </Link>
    );
  }

  const renderSubMenuList = (menuId: string, item) => {
    return (
      <div style={{ height: 96 }}>
        <div
          key={menuId}
          onMouseLeave={hideSubMenus}
          className={classes.subMenuList}
        >
          <div className={classes.subMenuListHeader}>
            <div>
              {item.title}
              <br />
              <div className={classes.subMenuListDescription}>
                {item.description}
              </div>
            </div>
            <div>
              {sectionIcons[menuId] && <img src={sectionIcons[menuId]} className={classes.menuIcon} />}
            </div>
          </div>
          {menuId === 'dashboard' ?
            renderDashboardSubMenu() :
            item.options.map((subMenuId) => {
              const subMenu = partsMap[subMenuId];
              if (!partsToShow[subMenuId] || !subMenu) {
                return null;
              }
              return renderSubMenu(subMenuId, subMenu);
            })
          }
        </div>
      </div>
    )
  }

  const renderMenuItemSingle = (menuId: string, item, isOnRoot) => {
    return (
      <Link
        to={() => handleRedirect(item.route, item.rootTitle)}
        onClick={closeSideMenu}
      ><div
        className={`${classes.menuItem}`}
        onMouseEnter={() => {
          if (!isOnRoot) {
            showSubMenus(menuId);
          }
        }}
        onMouseLeave={hideSubMenus}
        key={menuId}
      >
          <div>
            {isOnRoot ? item.rootTitle : item.title}
            <br />
            <div className={classes.menuDescription}>
              {item.description}
            </div>
          </div>
          <div>
            {sectionIcons[menuId] && <img src={sectionIcons[menuId]} className={classes.menuIcon} />}
          </div>
        </div>
      </Link>
    );
  }

  const renderDashboardMenuItem = (section) => {
    return (
      renderMenuItemMultiple(section.id, { title: section.title, description: '' })
    );
  }

  const renderMenuItemMultiple = (
    itemId: valueof<typeof partsConstants> | valueof<typeof sidebarSectionConstants>,
    section
  ) => {
    if (
      linksToHide.includes(itemId as valueof<typeof sidebarSectionConstants>) ||
      quickLinks.includes(itemId as valueof<typeof partsConstants>)
    ) {
      return;
    }

    if (expandedMenu === itemId) {
      return renderSubMenuList(itemId, section);
    }

    return (
      renderMenuItemSingle(itemId, section, false)
    );
  }

  const renderAdLinks = (menuId: string, item) => {
    return (
      <a href={websiteLinks[menuId]} target="_blank">
        <div
          className={`${classes.menuItem}`}
          key={menuId}
        >
          <div>
            {item.rootTitle || item.title} <CrownIcon />
            <br />
            <div className={classes.menuDescription}>
              {item.description}
            </div>
          </div>
          <div>
            {sectionIcons[menuId] && <img src={sectionIcons[menuId]} className={classes.menuIcon} />}
          </div>
        </div>
      </a>
    )
  }

  const getHiddenSection = () => {
    const hiddenSections = sectionList.filter(section => {
      const isOnRoot = !sidebarSectionsToShow[section.id] || section.id === 'root';
      if (isOnRoot) {
        return section.options.every(option => {
          return !partsMap[option] || !partsToShow[option];
        });
      }
      return false;
    });

    return hiddenSections;
  }

  const renderOtherPaidModules = () => {
    const hiddenSections = getHiddenSection();
    if (showShipperLogoInsteadOfShipsy){
      return null; // Do not show paid modules if shipper logo is shown
    }
    const otherPaidModules = hiddenSections.filter(section => {
      if (section.id !== 'root' && !linksToHide.includes(section.id) && paidFeatures.includes(section.id)) {
        return true;
      }
      return false;
    });
    return otherPaidModules.map(section => renderAdLinks(section.id, section));
  }

  const renderMenuItemGrid = () => {
    return (
      <div className={classes.menuItemGrid}>
        <div className={classes.menuGrid}>
          {sectionList.map((section: any, sectionIdx) => {
            if (section.id === 'dashboard' && !!partsToShow[section.id]) {
              return renderDashboardMenuItem(section);
            }
            const isOnRoot = !sidebarSectionsToShow[section.id] || section.id === 'root';
            const wrappingElemKey = section.id === 'root' ? sectionIdx : section.id;
            if (isOnRoot) {
              return section.options.map((option) => {
                if (!partsMap[option] || !partsToShow[option]) {
                  return null;
                }
                const item = partsMap[option];
                return renderMenuItemSingle(option, item, isOnRoot);
              });
            }

            return renderMenuItemMultiple(wrappingElemKey, section);
          })}
          {/* Showcasing other paid features */}
          {renderOtherPaidModules()}
        </div>
      </div>
    );
  }

  const renderSetupMenuItem = () => {
    const setupMenu = sectionList.find(section => section.id === 'setup');
    if (!setupMenu) {
      return;
    }

    return (
      <div className={classes.setupMenu}>
        <div className={classes.setupGrid}>
          {setupMenu.options.map((subMenuId) => {
            const subMenu = partsMap[subMenuId];
            if (!partsToShow[subMenuId] || !subMenu) {
              return null;
            }
            return (
              <Link
                to={() => handleRedirect(subMenu.route, subMenu.rootTitle)}
                key={subMenuId}
                onClick={closeSideMenu}
              >
                <div
                  className={classes.setupOptions}
                >
                  {subMenu.sectionTitle}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    )
  }

  const renderBody = () => {
    return (
      <div style={{ display: 'flex' }}>
        {renderMenuItemGrid()}
        {renderSetupMenuItem()}
      </div>
    )
  }

  const renderOverlay = () => {
    return (
      <div
        className={classes.overlay}
        onClick={closeSideMenu}
      />
    );
  }

  if (!open) {
    return null;
  }

  return (
    <>
      {renderOverlay()}
      <div
        className={classes.dropdown}
        ref={dropdownRef}
      >
        {renderHeader()}
        {renderBody()}
      </div>
    </>
  );
}

const mapStateToProps = (state) => {
  return {
    open: state.navigation.sideMenuOpen,
    dashboards: getStarredDashboards(state),
    sidebarSectionsToShow: state.authUser.sidebarSectionsToShow,
    partsToShow: state.authUser.partsToShow,
    type: state.masterData.userData.type,
    orgLogo: state.organisation?.orgDetails?.logoUrl,
    permissionsModule: state.masterData.modules,
    showShipperLogoInsteadOfShipsy: state.masterData.modules?.show_shipper_logo_instead_of_shipsy_logo,
    orgType: state.masterData.userData.type,
    showProcurement: state.masterData.modules?.show_inquiry || state.masterData.modules?.show_inquiry_bidding,
    defaultLandingPage: state.masterData?.modules?.default_route || '',
    userLandingPage: state.masterData?.userData?.user_metadata?.landing_page || '',
    discoverRatesPrefillData: state.masterData?.redirectPrefillData,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators({
    closeSideMenu,
  }, dispatch);
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

const SideMenuStyled = injectSheet(styles)(SideMenu);
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SideMenuStyled));
